/**
 * src/services/teleportExtensionPolicyService.ts
 *
 * Client bindings for the per-country **free teleport-extension policy**.
 *
 * Storage:
 *   - Backend mirror (no on-chain contract — same rationale as the
 *     other policy mirrors). See
 *     server/services/teleportExtensionPolicyMirror.js.
 *   - GET  /api/admin/teleport-extension-policy/:country  — public snapshot.
 *   - POST /api/admin/teleport-extension-policy           — admin write.
 *
 * Tri-state policy values:
 *   0 = NotSet     — no admin decision; resolves to the env-driven
 *                    global default (allowed by default).
 *   1 = Allowed    — server may auto-extend the AI-agent broadcast for
 *                    free while at least one teleport-attached viewer
 *                    is present, in `chunkSeconds` increments capped at
 *                    `maxTotalAutoExtensionSeconds` per session.
 *   2 = Disallowed — server may NOT auto-extend; broadcast cuts at
 *                    `scheduledEndAt` like a normal paid session.
 */

export const TELEPORT_EXTENSION_POLICY = {
  NOT_SET:    0,
  ALLOWED:    1,
  DISALLOWED: 2,
} as const;

export type TeleportExtensionPolicyValue =
  typeof TELEPORT_EXTENSION_POLICY[keyof typeof TELEPORT_EXTENSION_POLICY];

export interface TeleportExtensionPolicyConfig {
  policy:                       TeleportExtensionPolicyValue;
  chunkSeconds:                 number;
  maxTotalAutoExtensionSeconds: number;
  /**
   * `true` when no admin row exists for this country and the resolved
   * view above came from the env-driven defaults; `false` when an
   * explicit admin row is in effect. Lets the admin UI render a
   * "currently default — set explicitly to override" hint.
   */
  usingDefault: boolean;
  /** Raw mirror entry when one exists (for the "this row was last
   *  changed by Y on Z" panel). `null` when no admin override yet. */
  raw: {
    country?: number;
    policy?: number;
    chunkSeconds?: number;
    maxTotalAutoExtensionSeconds?: number;
    walletAddress?: string | null;
    reason?: string | null;
    policyVersion?: number;
    setAt?: string;
  } | null;
  defaults: {
    policy:                       TeleportExtensionPolicyValue;
    chunkSeconds:                 number;
    maxTotalAutoExtensionSeconds: number;
  };
}

const DEFAULT_BACKEND = (
  (typeof process !== 'undefined' && (process as any).env?.REACT_APP_BACKEND_URL) || ''
) as string;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${DEFAULT_BACKEND}${path}`, {
    ...(init || {}),
    headers: {
      'Content-Type': 'application/json',
      ...((init?.headers as Record<string, string>) || {}),
    },
  });
  const data = await resp.json().catch(() => ({} as any));
  if (!resp.ok || data.ok === false) {
    const detail = data?.details ? ` (${JSON.stringify(data.details)})` : '';
    throw new Error((data?.error || `request_failed_${resp.status}`) + detail);
  }
  return data as T;
}

function _coerceTriState(n: any): TeleportExtensionPolicyValue {
  const v = Number(n);
  if (v === 1 || v === 2) return v as TeleportExtensionPolicyValue;
  return TELEPORT_EXTENSION_POLICY.NOT_SET;
}

export async function getTeleportExtensionPolicy(
  country: number,
): Promise<TeleportExtensionPolicyConfig> {
  try {
    const data = await request<any>(`/api/admin/teleport-extension-policy/${country}`);
    return {
      policy:                       _coerceTriState(data.policy),
      chunkSeconds:                 Number(data.chunkSeconds) || 60,
      maxTotalAutoExtensionSeconds: Number(data.maxTotalAutoExtensionSeconds) || 1800,
      usingDefault:                 !!data.usingDefault,
      raw:                          data.raw || null,
      defaults: {
        policy:                       _coerceTriState(data?.defaults?.policy),
        chunkSeconds:                 Number(data?.defaults?.chunkSeconds) || 60,
        maxTotalAutoExtensionSeconds: Number(data?.defaults?.maxTotalAutoExtensionSeconds) || 1800,
      },
    };
  } catch {
    return {
      policy:                       TELEPORT_EXTENSION_POLICY.NOT_SET,
      chunkSeconds:                 60,
      maxTotalAutoExtensionSeconds: 1800,
      usingDefault:                 true,
      raw:                          null,
      defaults: {
        policy:                       TELEPORT_EXTENSION_POLICY.ALLOWED,
        chunkSeconds:                 60,
        maxTotalAutoExtensionSeconds: 1800,
      },
    };
  }
}

export interface SetTeleportExtensionPolicyInput {
  walletAddress?:               string;
  policy:                       TeleportExtensionPolicyValue;
  chunkSeconds?:                number;
  maxTotalAutoExtensionSeconds?:number;
  reason?:                      string | null;
  policyVersion?:               number;
}

export async function setTeleportExtensionPolicy(
  country: number,
  input: SetTeleportExtensionPolicyInput,
): Promise<TeleportExtensionPolicyConfig> {
  const data = await request<any>(`/api/admin/teleport-extension-policy`, {
    method: 'POST',
    body: JSON.stringify({ country, ...input }),
  });
  return {
    policy:                       _coerceTriState(data.policy),
    chunkSeconds:                 Number(data.chunkSeconds) || 60,
    maxTotalAutoExtensionSeconds: Number(data.maxTotalAutoExtensionSeconds) || 1800,
    usingDefault:                 false,
    raw: {
      country:                       Number(data.country),
      policy:                        Number(data.policy),
      chunkSeconds:                  Number(data.chunkSeconds),
      maxTotalAutoExtensionSeconds:  Number(data.maxTotalAutoExtensionSeconds),
      walletAddress:                 data.walletAddress ?? null,
      reason:                        data.reason ?? null,
      policyVersion:                 Number(data.policyVersion) || 0,
      setAt:                         data.setAt,
    },
    defaults: {
      policy:                       _coerceTriState(data?.defaults?.policy),
      chunkSeconds:                 Number(data?.defaults?.chunkSeconds) || 60,
      maxTotalAutoExtensionSeconds: Number(data?.defaults?.maxTotalAutoExtensionSeconds) || 1800,
    },
  };
}

export function teleportExtensionPolicyLabel(v: TeleportExtensionPolicyValue): string {
  switch (v) {
    case TELEPORT_EXTENSION_POLICY.ALLOWED:    return 'Allowed';
    case TELEPORT_EXTENSION_POLICY.DISALLOWED: return 'Disallowed';
    default:                                   return 'Not set (default = allowed)';
  }
}
